/**
 * Progress View — Timeline of assignments, completions, and Navigator observations.
 * Per spec Section 4.5. Thread-aware: "Current Thread" vs "All Threads" toggle.
 */

import { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertCircle, TrendingUp, Download, CalendarPlus, Lock, Loader2, Target, MessageSquare, ClipboardList, Sparkles, AlertTriangle, MessageCircle, Scale } from 'lucide-react';
import { exportProgressPdf, exportAssignmentsIcs, getArcs } from '../lib/api';
import UpgradePrompt from './UpgradePrompt';

function AssignmentItem({ assignment }) {
  const statusConfig = {
    pending: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-900/20', label: 'Pending' },
    submitted: { icon: AlertCircle, color: 'text-blue-400', bg: 'bg-blue-900/20', label: 'Submitted' },
    evaluated: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-900/20', label: 'Evaluated' }
  };

  const status = statusConfig[assignment.status] || statusConfig.pending;
  const Icon = status.icon;

  return (
    <div className={`${status.bg} border border-slate-700/50 rounded-lg p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={status.color} />
        <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
        <span className="ml-auto text-xs text-slate-500">
          {new Date(assignment.issued_at).toLocaleDateString()}
        </span>
      </div>
      <p className="text-sm text-slate-200 mb-1">{assignment.goal_text}</p>
      {assignment.phase === 2 && assignment.who_it_helps && (
        <p className="text-xs text-slate-500">Helps: {assignment.who_it_helps}</p>
      )}
      {assignment.evaluation_text && (
        <div className="mt-2 pt-2 border-t border-slate-700/50">
          <p className="text-xs text-slate-400">{assignment.evaluation_text}</p>
          {assignment.evaluation_score && (
            <span className="text-xs text-slate-500 mt-1">Score: {assignment.evaluation_score}/5</span>
          )}
        </div>
      )}
    </div>
  );
}

const PATTERN_LABELS = {
  strong_follow_through: 'Strong Follow-Through',
  progress_momentum: 'Progress Momentum',
  early_delivery: 'Early Delivery',
  last_minute_delivery: 'Last-Minute Delivery',
  no_proactive_communication: 'No Proactive Communication',
  inward_focus: 'Inward Focus',
  avoidance: 'Avoidance',
  incomplete_submission: 'Incomplete Submission',
  over_commitment: 'Over-Commitment',
  self_sabotage: 'Self-Sabotage',
  external_blame: 'External Blame',
  isolation: 'Isolation',
  perfectionism: 'Perfectionism',
};

const POSITIVE_PATTERNS = new Set(['strong_follow_through', 'progress_momentum', 'early_delivery']);

// One-line cost named when a negative pattern is recurring (3+).
const PATTERN_COST = {
  last_minute_delivery: 'Deadlines keep getting cut close — quality and stress both pay for it.',
  no_proactive_communication: "People hear from you only once it's already a problem.",
  inward_focus: 'Lots of reflection, little reaching outward — momentum comes from contribution.',
  avoidance: "The thing you keep stepping around is still there — and it's growing.",
  incomplete_submission: "Work goes out unfinished, so it doesn't land the way it could.",
  over_commitment: "You're saying yes past your capacity — something will give.",
  self_sabotage: 'You undercut your own progress right when it starts working.',
  external_blame: "When it's always outside your control, nothing inside it changes.",
  isolation: "Going it alone is costing you the help that would move this faster.",
  perfectionism: 'Waiting for perfect means it ships late — or never.',
};

const PATTERN_AFFIRM = {
  strong_follow_through: "You do what you say you'll do. Keep banking it.",
  progress_momentum: "You're stacking wins — the momentum is real.",
  early_delivery: 'You deliver ahead of the deadline. Rare, and worth protecting.',
};

function labelFor(type) {
  return PATTERN_LABELS[type] || type.replace(/_/g, ' ');
}

/**
 * PatternItem — escalates visual weight with recurrence, mirroring the AI's own
 * confrontation threshold (the engine starts calling patterns out at 3+).
 * Positive patterns render green as earned proof. drive_level softens tone:
 * Coach level caps at amber and gentles the copy.
 */
function PatternItem({ pattern, driveLevel = 'mentor', onConfront }) {
  const type = pattern.pattern_type;
  const count = pattern.recurrence_count || 1;
  const label = labelFor(type);
  const gentle = driveLevel === 'coach';

  if (POSITIVE_PATTERNS.has(type)) {
    return (
      <div className="rounded-lg border border-green-800/40 bg-green-950/20 p-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-green-400 flex-shrink-0" />
          <span className="text-sm font-medium text-green-300">{label}</span>
          <span className="ml-auto text-[11px] text-green-500/80">{count}× — earned</span>
        </div>
        {PATTERN_AFFIRM[type] && (
          <p className="text-xs text-slate-400 mt-1.5 pl-6 leading-snug">{PATTERN_AFFIRM[type]}</p>
        )}
      </div>
    );
  }

  // Negative pattern below the confrontation threshold — keep it quiet.
  if (count < 3) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-slate-700/40 bg-slate-800/20 px-3 py-2.5">
        <TrendingUp size={14} className="text-slate-500 flex-shrink-0" />
        <span className="text-sm text-slate-300">{label}</span>
        <span className="text-xs text-slate-500 ml-auto">seen {count}×</span>
      </div>
    );
  }

  // Recurring (3+) — confront it. Red at 5+ unless Coach level keeps it amber.
  const severe = count >= 5 && !gentle;
  const tone = severe
    ? { ring: 'border-red-800/50', wash: 'bg-red-950/20', icon: 'text-red-400', label: 'text-red-300', count: 'text-red-400/80', btn: 'bg-red-900/40 hover:bg-red-900/60 text-red-200' }
    : { ring: 'border-amber-800/50', wash: 'bg-amber-950/20', icon: 'text-amber-400', label: 'text-amber-200', count: 'text-amber-400/80', btn: 'bg-amber-900/30 hover:bg-amber-900/50 text-amber-200' };
  const cost = PATTERN_COST[type];

  return (
    <div className={`rounded-lg border ${tone.ring} ${tone.wash} p-3`}>
      <div className="flex items-center gap-2">
        <AlertTriangle size={14} className={`${tone.icon} flex-shrink-0`} />
        <span className={`text-sm font-medium ${tone.label}`}>{label}</span>
        <span className={`ml-auto text-[11px] font-medium ${tone.count}`}>seen {count}× — recurring</span>
      </div>
      {cost && <p className="text-xs text-slate-400 mt-1.5 pl-6 leading-snug">{cost}</p>}
      {onConfront && (
        <button
          onClick={() => onConfront(label)}
          className={`mt-2.5 ml-6 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors min-h-tap sm:min-h-0 ${tone.btn}`}
        >
          <MessageCircle size={13} />
          {gentle ? 'Want to talk about this?' : 'Talk to me about this'}
        </button>
      )}
    </div>
  );
}

const ACCURACY_BADGE = {
  accurate: { label: 'Accurate', cls: 'bg-green-900/40 text-green-300' },
  partially_accurate: { label: 'Partially accurate', cls: 'bg-amber-900/40 text-amber-200' },
  inaccurate: { label: 'Inaccurate', cls: 'bg-red-900/40 text-red-300' },
};

/**
 * DecisionItem — one entry in the decision journal. Open decisions show the
 * prediction and review date (amber when due); reviewed ones show how the
 * prediction held up. The journal is written and scored in session — this
 * view is the read-only calibration record.
 */
function DecisionItem({ decision }) {
  const isOpen = decision.status === 'open';
  const isDue = isOpen && decision.is_due;
  const badge = ACCURACY_BADGE[decision.prediction_accuracy];

  return (
    <div className={`rounded-lg border p-3 ${
      isDue ? 'border-amber-800/50 bg-amber-950/20' : 'border-slate-700/50 bg-slate-800/20'
    }`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Scale size={14} className={isDue ? 'text-amber-400' : 'text-slate-500'} />
        <span className="text-sm text-slate-200">{decision.decision_text}</span>
        {isOpen ? (
          <span className={`ml-auto text-[11px] whitespace-nowrap ${isDue ? 'text-amber-400 font-medium' : 'text-slate-500'}`}>
            {isDue ? 'review due' : `review ${new Date(decision.review_date).toLocaleDateString()}`}
          </span>
        ) : decision.status === 'abandoned' ? (
          <span className="ml-auto text-[11px] text-slate-500 whitespace-nowrap">abandoned</span>
        ) : badge && (
          <span className={`ml-auto text-[11px] px-1.5 py-0.5 rounded whitespace-nowrap ${badge.cls}`}>{badge.label}</span>
        )}
      </div>
      <p className="text-xs text-slate-400 pl-6 leading-snug">
        Predicted: &ldquo;{decision.predicted_outcome}&rdquo;
        {decision.confidence ? ` (confidence ${decision.confidence}/10)` : ''}
      </p>
      {decision.actual_outcome && (
        <p className="text-xs text-slate-400 pl-6 leading-snug mt-1">
          Actual: {decision.actual_outcome}
        </p>
      )}
      {decision.review_notes && (
        <p className="text-xs text-slate-500 pl-6 leading-snug mt-1 italic">{decision.review_notes}</p>
      )}
    </div>
  );
}

function ArcCard({ arc }) {
  const total = arc.milestones?.length || 0;
  const completed = arc.milestones?.filter(m => m.status === 'completed').length || 0;
  const missed = arc.milestones?.filter(m => m.status === 'missed').length || 0;
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const daysRemaining = arc.days_remaining ?? '?';

  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Target size={14} className="text-blue-400" />
        <span className="text-sm font-medium text-slate-200">{arc.title}</span>
        {arc.thread_name && (
          <span className="text-[11px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded ml-auto">
            {arc.thread_name}
          </span>
        )}
      </div>

      {arc.target_metric && (
        <p className="text-xs text-slate-400 mb-2">Target: {arc.target_metric}</p>
      )}

      {/* Progress bar */}
      <div className="mb-2">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{completed}/{total} milestones</span>
          <span>{daysRemaining} days left</span>
        </div>
        <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              daysRemaining < 14 && progressPct < 50 ? 'bg-amber-500' : 'bg-blue-500'
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Milestone timeline */}
      {arc.milestones && arc.milestones.length > 0 && (
        <div className="space-y-1 mt-2">
          {arc.milestones.map((m, i) => (
            <div key={m.id || i} className="flex items-center gap-2 text-xs">
              {m.status === 'completed' ? (
                <CheckCircle size={11} className="text-green-400 flex-shrink-0" />
              ) : m.status === 'missed' ? (
                <AlertCircle size={11} className="text-red-400 flex-shrink-0" />
              ) : (
                <Clock size={11} className="text-slate-500 flex-shrink-0" />
              )}
              <span className={m.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-300'}>
                {m.title}
              </span>
              {m.target_date && (
                <span className="text-slate-600 ml-auto">{new Date(m.target_date).toLocaleDateString()}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Progress notes */}
      {arc.progress_notes && arc.progress_notes.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-700/50">
          {arc.progress_notes.slice(-3).map((n, i) => (
            <p key={i} className="text-[11px] text-slate-500">
              {typeof n === 'object' ? `${n.date}: ${n.note}` : n}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function ThreadOverview({ threads }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-slate-400 mb-2">All Threads</h3>
      <div className="space-y-2">
        {threads.map(thread => (
          <div key={thread.id} className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: thread.color || '#3B82F6' }}
              />
              <span className="text-sm font-medium text-slate-200">{thread.name}</span>
              {thread.status !== 'active' && (
                <span className="text-[11px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded">{thread.status}</span>
              )}
              <span className="ml-auto text-xs text-slate-500">
                {thread.session_count || 0} sessions
              </span>
            </div>
            <div className="flex gap-4 text-xs text-slate-500 ml-4.5">
              <span>{thread.pending_assignments || 0} pending</span>
              {thread.last_activity_at && (
                <span>Last active: {new Date(thread.last_activity_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProgressView({ assignments, patterns, sessions, threads, activeThreadId, subscription, driveLevel = 'mentor', onConfrontPattern, decisions }) {
  const [viewMode, setViewMode] = useState('thread'); // 'thread' | 'all'
  const [exporting, setExporting] = useState(false);
  const [exportingIcs, setExportingIcs] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [limitError, setLimitError] = useState(null);
  const [arcs, setArcs] = useState([]);

  const isEntrepreneur = subscription?.tier === 'entrepreneur';

  useEffect(() => {
    if (!isEntrepreneur) return;
    const threadId = viewMode === 'thread' ? activeThreadId : null;
    getArcs(threadId)
      .then(res => setArcs(res.arcs || []))
      .catch(() => setArcs([]));
  }, [isEntrepreneur, activeThreadId, viewMode]);

  const filteredAssignments = viewMode === 'thread' && activeThreadId
    ? assignments.filter(a => a.thread_id === activeThreadId)
    : assignments;

  const filteredSessions = viewMode === 'thread' && activeThreadId
    ? sessions.filter(s => s.thread_id === activeThreadId)
    : sessions;

  const handleExport = async () => {
    if (!isEntrepreneur) {
      setLimitError({
        error: 'PDF export is an Entrepreneur feature',
        upgrade_tier: 'entrepreneur',
        upgrade_message: 'Upgrade to Entrepreneur to export your progress as a PDF report.'
      });
      return;
    }

    setExporting(true);
    setExportError(null);
    try {
      const threadId = viewMode === 'thread' ? activeThreadId : null;
      const result = await exportProgressPdf(threadId);
      if (result.upgrade_tier) {
        setLimitError(result);
      }
    } catch (err) {
      setExportError('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportIcs = async () => {
    if (!isEntrepreneur) {
      setLimitError({
        error: 'Calendar export is an Entrepreneur feature',
        upgrade_tier: 'entrepreneur',
        upgrade_message: 'Upgrade to Entrepreneur to export assignments as calendar tasks.'
      });
      return;
    }

    setExportingIcs(true);
    setExportError(null);
    try {
      const threadId = viewMode === 'thread' ? activeThreadId : null;
      const result = await exportAssignmentsIcs(threadId, 'pending,submitted');
      if (result.upgrade_tier) {
        setLimitError(result);
      }
    } catch (err) {
      setExportError('Failed to export calendar. Please try again.');
    } finally {
      setExportingIcs(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-medium text-slate-200">Progress</h2>

        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
              isEntrepreneur
                ? 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                : 'bg-slate-800/50 text-slate-500 hover:text-slate-400'
            }`}
            title={isEntrepreneur ? 'Export progress as PDF' : 'Entrepreneur feature'}
          >
            {exporting ? (
              <Loader2 size={13} className="animate-spin" />
            ) : isEntrepreneur ? (
              <Download size={13} />
            ) : (
              <Lock size={13} />
            )}
            Export PDF
          </button>

          <button
            onClick={handleExportIcs}
            disabled={exportingIcs}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors ${
              isEntrepreneur
                ? 'bg-slate-700 hover:bg-slate-600 text-slate-200'
                : 'bg-slate-800/50 text-slate-500 hover:text-slate-400'
            }`}
            title={isEntrepreneur ? 'Export tasks (.ics) — works with Google Calendar & Apple Reminders' : 'Entrepreneur feature'}
          >
            {exportingIcs ? (
              <Loader2 size={13} className="animate-spin" />
            ) : isEntrepreneur ? (
              <CalendarPlus size={13} />
            ) : (
              <Lock size={13} />
            )}
            Export Tasks
          </button>

          {threads && threads.length > 1 && (
            <div className="flex bg-slate-800/50 rounded-md p-0.5">
              <button
                onClick={() => setViewMode('thread')}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  viewMode === 'thread' ? 'bg-slate-700 text-slate-200' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                Current Thread
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  viewMode === 'all' ? 'bg-slate-700 text-slate-200' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                All Threads
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Goal Arcs (Entrepreneur only) */}
      {isEntrepreneur && arcs.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 mb-2">Goal Arcs</h3>
          <div className="space-y-3">
            {arcs.map(arc => <ArcCard key={arc.id} arc={arc} />)}
          </div>
        </div>
      )}

      {/* Upgrade prompt */}
      {limitError && (
        <UpgradePrompt limitError={limitError} onDismiss={() => setLimitError(null)} />
      )}

      {/* Export error */}
      {exportError && (
        <p className="text-xs text-red-400">{exportError}</p>
      )}

      {/* Thread overview (all-threads mode) */}
      {viewMode === 'all' && threads && threads.length > 0 && (
        <ThreadOverview threads={threads} />
      )}

      {/* Decision Journal — predictions made vs how they held up */}
      {decisions && (decisions.open?.length > 0 || decisions.reviewed?.length > 0) && (
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-400">Decision Journal</h3>
            {(() => {
              const tr = decisions.track_record || {};
              const total = (tr.accurate || 0) + (tr.partially_accurate || 0) + (tr.inaccurate || 0);
              return total > 0 ? (
                <span className="text-[11px] text-slate-500">
                  Calibration: {tr.accurate || 0} accurate · {tr.partially_accurate || 0} partial · {tr.inaccurate || 0} off — of {total} scored
                </span>
              ) : null;
            })()}
          </div>
          <div className="space-y-2">
            {(decisions.open || []).map(d => <DecisionItem key={d.id} decision={d} />)}
            {(decisions.reviewed || []).map(d => <DecisionItem key={d.id} decision={d} />)}
          </div>
        </div>
      )}

      {/* Patterns */}
      {patterns && patterns.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 mb-2">Observed Patterns</h3>
          <div className="space-y-2">
            {patterns.map((p, i) => (
              <PatternItem key={i} pattern={p} driveLevel={driveLevel} onConfront={onConfrontPattern} />
            ))}
          </div>
        </div>
      )}

      {/* Assignment timeline */}
      <div>
        <h3 className="text-sm font-medium text-slate-400 mb-2">Assignments</h3>
        {(!filteredAssignments || filteredAssignments.length === 0) ? (
          <div className="bg-slate-800/30 border border-dashed border-slate-700/50 rounded-lg px-6 py-8 text-center">
            <ClipboardList size={28} className="text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-300 font-medium mb-1">No assignments yet</p>
            <p className="text-xs text-slate-500 max-w-xs mx-auto flex items-center justify-center gap-1.5">
              Open the
              <span className="inline-flex items-center gap-1 text-slate-400">
                <MessageSquare size={11} /> Coach
              </span>
              tab to get your first one.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredAssignments.map((a, i) => <AssignmentItem key={i} assignment={a} />)}
          </div>
        )}
      </div>

      {/* Session history */}
      {filteredSessions && filteredSessions.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 mb-2">Recent Sessions</h3>
          <div className="space-y-2">
            {filteredSessions.map((s, i) => (
              <div key={i} className="bg-slate-800/20 border border-slate-700/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-slate-500">
                    {new Date(s.started_at).toLocaleDateString()}
                  </span>
                  {s.crisis_flagged && (
                    <span className="text-xs bg-red-900/30 text-red-400 px-2 py-0.5 rounded">crisis</span>
                  )}
                </div>
                {s.summary_text && (
                  <p className="text-sm text-slate-300">{s.summary_text}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
